import { useState } from "react";

/**
 * Shared hook for viewing uploaded documents in all environments.
 *
 * CW Browser (Electron without shell.openExternal) blocks all new-window
 * opens — window.open returns null, <a target=_blank> is suppressed.
 * When that happens, the document is fetched as a blob and shown in an
 * inline modal within the current page.
 *
 * Returns { openDocument, docViewer, closeDocViewer } for use with the
 * DocViewerModal JSX rendered by the consuming component.
 */
const useDocumentViewer = () => {
  const [docViewer, setDocViewer] = useState({ visible: false, url: null, isPdf: false });

  const openDocument = (fileUrl) => {
    if (!fileUrl) return;
    console.log("[ViewDoc] openDocument:", fileUrl, "| electronAPI:", !!window.electronAPI, "| openExternal:", !!window.electronAPI?.shell?.openExternal);
    if (window.electronAPI?.shell?.openExternal) {
      console.log("[ViewDoc] path: shell.openExternal");
      window.electronAPI.shell.openExternal(fileUrl);
      return;
    }
    const encoded = encodeURI(fileUrl);
    // CW Browser: has electronAPI but no shell.openExternal — window.open may return a
    // non-null object that Electron immediately discards, causing silent failure.
    // Detect this combination explicitly and skip window.open entirely.
    const isCwBrowser = !!window.electronAPI && !window.electronAPI?.shell?.openExternal;
    if (!isCwBrowser) {
      const win = window.open(encoded, "_blank");
      console.log("[ViewDoc] window.open result:", win ? "opened" : "null/blocked");
      if (win) return;
    }
    const isPdf = fileUrl.split("?")[0].toLowerCase().endsWith(".pdf");
    console.log("[ViewDoc] path: blob→inline modal, isPdf:", isPdf);
    fetch(encoded)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        console.log("[ViewDoc] blob ready, opening inline viewer:", blobUrl);
        setDocViewer({ visible: true, url: blobUrl, isPdf });
      })
      .catch((err) => console.error("[ViewDoc] blob fetch failed:", err));
  };

  const closeDocViewer = () => {
    if (docViewer.url) URL.revokeObjectURL(docViewer.url);
    setDocViewer({ visible: false, url: null, isPdf: false });
  };

  return { openDocument, docViewer, closeDocViewer };
};

export default useDocumentViewer;
